import type Joi from 'joi';
import { TRegionResource, TSpecSummary, TSubRegionResource } from './utilityTypes';
import {
  BracketFormat,
  IEventBracketSummary,
  IEventBracketTeamShortDetails,
  IPublishedBracketSummary,
  IUserRegistrationBracket
} from './eventBracketTypes';
import { TEventVideoEntityType, TEventVideoTags, TVideoType } from '../common/videoUtils';
import { TClientAffixData } from './keystoneAffixTypes';
import { TShortStorageItemSummary } from './storageTypes';
import { TTeamSummary } from './teamTypes';
import type { IEventInstance } from '../backend/models/events/eventModelFactory';
import type { ISharedUserInstance } from '../backend/models/sharedUserModel';
import type { ISharedGroupCharterInstance } from '../backend/models/sharedGroupCharterModel';
import { TResultStandingAggregationList } from './eventStandingsTypes';

export enum EventCategory {
  BLIZZARD = 'blizzard',
  COMMUNITY = 'community'
}

export enum EventType {
  MDI = 'mythic-dungeon-international',
  AWC = 'arena-world-championship',
  VARIETY_SHOW = 'variety-show',
  Legacy = 'legacy'
}

export enum EventNamespace {
  BlizzardMDI = EventType.MDI,
  BlizzardAWC = EventType.AWC,
  BlizzardVarietyShow = EventType.VARIETY_SHOW,
  CommunityMDI = 'community-mythic-plus-event',
  CommunityAWC = 'community-arena-event',
  CommunityVarietyShow = 'community-variety-show'
}

export const EventNamespaces = {
  [EventType.MDI]: {
    [EventCategory.BLIZZARD]: EventNamespace.BlizzardMDI,
    [EventCategory.COMMUNITY]: EventNamespace.CommunityMDI
  },
  [EventType.AWC]: {
    [EventCategory.BLIZZARD]: EventNamespace.BlizzardAWC,
    [EventCategory.COMMUNITY]: EventNamespace.CommunityAWC
  },
  [EventType.VARIETY_SHOW]: {
    [EventCategory.BLIZZARD]: EventNamespace.BlizzardVarietyShow,
    [EventCategory.COMMUNITY]: EventNamespace.CommunityVarietyShow
  }
};

export enum EventMode {
  WAITING_REGISTRATION = 'waiting_registration',
  ACTIVE = 'active',
  WAITING_EVENT_START = 'waiting_event_start',
  STARTED = 'started',
  COMPLETED = 'completed',
  UNKNOWN = 'unknown'
}
export interface IEventVideoSummary {
  entityId: number;
  videoType: TVideoType;
  entityType: TEventVideoEntityType;
  videoId: string;
  title: string;
  ordinal: number;
  tags: TEventVideoTags[];
  id: number;
  videoTimestampSeconds?: number | null;
  videoDurationSeconds?: number | null;
  failedLookupAt?: Date | null;
  lastAvailableAt?: Date;
  bracketGroup?: string | null;
}
export enum EsportEventType {
  MDI = 'mythic-dungeon-international',
  AWC = 'arena-world-championship',
  OTHER = 'other',
  UNLISTED = 'unlisted' // JAH 8/23/2024 - What is this for?
}
export interface IEventContentSummary {
  id: number;
  title: string;
  content: string;
  showTab: boolean;
  bracketSwitcher: boolean;
  ordinal: number;
  html?: string;
  type: EventContentSlug;
  data: string;
  scope: EventContentScope;
}
/** Shape of the JSON stored in `IEventContentSummary.data` (`events_contents.data`). */
export interface IEventContentData {
  bracketSlug?: string;
  seasonSlug?: string;
  region?: string;
  scope?: EventContentScope;
  /** Ordered, authoritative curation of the tab's Bracket Switcher; empty/absent = all eligible brackets. */
  bracketSwitcherSlugs?: string[];
}
export interface IEventRegistrationStepSummary {
  id?: number;
  title: string;
  subtitle: string;
  description: string;
  type: RegistrationStepType;
  options: IEventRegistrationStepOption[];
  ordinal: number;
  subtitleHTML?: string;
  descriptionHTML?: string;
}

export type TEventUserRegistrationStepData = Record<string, any>;

export interface IEventRegistrationUserStepData extends IEventRegistrationStepSummary {
  data?: TEventUserRegistrationStepData;
}

export interface IUserRegistrationStepData {
  data?: TEventUserRegistrationStepData;
  type: RegistrationStepType;
  options: IEventRegistrationStepOption[];
}

export enum EventContentSlug {
  REGISTER = 'register',
  TEAMS = 'teams',
  BRACKETS = 'brackets',
  TIME_TRIALS_DUNGEONS = 'time-trials-dungeons',
  TIME_TRIALS_TEAMS = 'time-trials-teams',
  THE_GREAT_PUSH_DUNGEONS = 'the-great-push-dungeons',
  THE_GREAT_PUSH_LEADERBOARD = 'the-great-push-leaderboard',
  TIMELINE = 'timeline',
  LINK = 'link',
  BREAK_THE_META_RUNS = 'break-the-meta-runs',
  BREAK_THE_META_CLASSES = 'break-the-meta-classes',
  PLUNDERSTORM_CREATOR_ROYALE = 'plunderstorm-creator-royale',
  STANDARD_RUNS = 'standard-runs',
  STANDARD_TEAMS = 'standard-teams'
}
export const enum RegistrationStepType {
  SYNC_YOUR_BLIZZARD_ACCOUNT = 'sync-your-blizzard-account',
  CLAIM_YOUR_CHARACTERS = 'claim-your-characters',
  SETUP_RAIDERIO_CLIENT = 'setup-raiderio-client',
  STREAMER_REGISTRATION = 'streamer-registration',
  SELECT_TEAM = 'select-team',
  AGREE_TO_TERMS = 'agree-to-terms',
  SYNC_MYTHIC_PLUS_CHARACTERS = 'sync-mythic-plus-characters',
  VERIFY_SOCIAL_AND_CONTACT_DETAILS = 'verify-social-and-contact-details',
  BUILDER = 'builder',
  BB_CODE = 'bb-code'
}
export const enum EventPrivacy {
  PUBLIC = 'public',
  UNLISTED = 'unlisted',
  PRIVATE = 'private'
}

export type TEventStepOption<T extends keyof TRegistrationStepOptionLabelsByStepType> = {
  label: TRegistrationStepOptionLabelsByStepType[T];
  mode: RegistrationStepOptionMode;
};

export type TRegistrationStepOptionBuilderLabel = `builder_${string}`;

export enum StaticRegistrationStepOptionLabel {
  BATTLE_TAG = 'battle-tag',
  BNET_ID = 'bnet-id',
  BNET_REGIONS = 'bnet-regions',
  TOURNAMENT_REGION = 'tournament-region',
  BLIZZARD_ACCOUNT_EMAIL = 'blizzard-account-email',
  CONFIRM_BLIZZARD_ACCOUNT_EMAIL = 'confirm-blizzard-account-email',
  ACCOUNT_EMAIL = 'account-email',
  DISCORD_TAG = 'discord-tag',
  TWITTER_PROFILE = 'twitter-profile',
  DESKTOP_CLIENT_INSTALLED = 'desktop-client-installed',
  DESKTOP_CLIENT_HAS_BEEN_RUN_RECENTLY = 'desktop-client-has-been-run-recently',
  TWITCH_PROFILE = 'twitch-profile',
  DOUYU_PROFILE = 'douyu-profile',
  WECHAT_ID = 'wechat-id',
  STREAMER_SCHEDULE = 'streamer-schedule',
  STREAMER_AGREED = 'streamer-agreed',
  YOUTUBE_PROFILE = 'youtube-profile',
  CHOOSE_TEAM = 'choose-team',
  AGREE_TO_TERMS = 'agree-to-terms',
  CHARACTER = 'character',
  ROLE = 'role',
  YOUR_TOP_MYTHIC_PLUS_SCORE_IS_AT_LEAST = 'your-top-mythic-plus-score-is-at-least',
  YOUR_TOP_MYTHIC_PLUS_SCORE_DOES_NOT_EXCEED = 'your-top-mythic-plus-score-does-not-exceed',
  IDEAL_SCORE_MIN_VALUE = 'ideal-score-min-value',
  IDEAL_SCORE_MAX_VALUE = 'ideal-score-max-value',
  IDEAL_SCORE_TICKETS = 'ideal-score-tickets',
  CONFIRM_REGISTRATION_DATA = 'confirm-registration-data',
  CONFIRM_CONTACT_DETAILS = 'confirm-contact-details',
  CLAIMED_CHARACTERS = 'claimed-characters'
}

export type TRegistrationStepOptionLabel = StaticRegistrationStepOptionLabel | TRegistrationStepOptionBuilderLabel;

export type TRegistrationStepOptionLabelsByStepType = {
  [RegistrationStepType.SYNC_YOUR_BLIZZARD_ACCOUNT]:
    | StaticRegistrationStepOptionLabel.BATTLE_TAG
    | StaticRegistrationStepOptionLabel.TOURNAMENT_REGION
    | StaticRegistrationStepOptionLabel.BLIZZARD_ACCOUNT_EMAIL
    | StaticRegistrationStepOptionLabel.CONFIRM_BLIZZARD_ACCOUNT_EMAIL
    | StaticRegistrationStepOptionLabel.CONFIRM_REGISTRATION_DATA;
  [RegistrationStepType.CLAIM_YOUR_CHARACTERS]:
    | StaticRegistrationStepOptionLabel.CLAIMED_CHARACTERS
    | StaticRegistrationStepOptionLabel.CONFIRM_REGISTRATION_DATA;
  [RegistrationStepType.AGREE_TO_TERMS]: StaticRegistrationStepOptionLabel.AGREE_TO_TERMS;
  [RegistrationStepType.STREAMER_REGISTRATION]:
    | StaticRegistrationStepOptionLabel.TWITCH_PROFILE
    | StaticRegistrationStepOptionLabel.DOUYU_PROFILE
    | StaticRegistrationStepOptionLabel.YOUTUBE_PROFILE;
  [RegistrationStepType.SYNC_MYTHIC_PLUS_CHARACTERS]:
    | StaticRegistrationStepOptionLabel.CHARACTER
    | StaticRegistrationStepOptionLabel.ROLE
    | StaticRegistrationStepOptionLabel.YOUR_TOP_MYTHIC_PLUS_SCORE_DOES_NOT_EXCEED
    | StaticRegistrationStepOptionLabel.YOUR_TOP_MYTHIC_PLUS_SCORE_IS_AT_LEAST
    | StaticRegistrationStepOptionLabel.IDEAL_SCORE_MIN_VALUE
    | StaticRegistrationStepOptionLabel.IDEAL_SCORE_MAX_VALUE
    | StaticRegistrationStepOptionLabel.IDEAL_SCORE_TICKETS;
  [RegistrationStepType.SELECT_TEAM]: StaticRegistrationStepOptionLabel.CHOOSE_TEAM;
  [RegistrationStepType.SETUP_RAIDERIO_CLIENT]:
    | StaticRegistrationStepOptionLabel.DESKTOP_CLIENT_INSTALLED
    | StaticRegistrationStepOptionLabel.DESKTOP_CLIENT_HAS_BEEN_RUN_RECENTLY;
  [RegistrationStepType.VERIFY_SOCIAL_AND_CONTACT_DETAILS]:
    | StaticRegistrationStepOptionLabel.ACCOUNT_EMAIL
    | StaticRegistrationStepOptionLabel.WECHAT_ID
    | StaticRegistrationStepOptionLabel.DISCORD_TAG
    | StaticRegistrationStepOptionLabel.TWITCH_PROFILE
    | StaticRegistrationStepOptionLabel.DOUYU_PROFILE
    | StaticRegistrationStepOptionLabel.YOUTUBE_PROFILE
    | StaticRegistrationStepOptionLabel.TWITTER_PROFILE
    | StaticRegistrationStepOptionLabel.CONFIRM_REGISTRATION_DATA;
  [RegistrationStepType.BUILDER]: TRegistrationStepOptionBuilderLabel;
  [RegistrationStepType.BB_CODE]: never;
};

export enum BuilderStepOptionType {
  Input = 'input',
  Textarea = 'textarea',
  Checkbox = 'checkbox',
  Radio = 'radio',
  Slider = 'slider',
  Select = 'select',
  Switch = 'switch'
}

export const enum TeamType {
  MYTHICPLUS = 'mythic-plus',
  EVENT = 'event'
}

export type TRegistrationStepOptionValidatePatterns = {
  [key: string]: Joi.Schema;
};

export interface IStaticRegistrationStepOption {
  label: TRegistrationStepOptionLabel;
  supportsModes?: RegistrationStepOptionMode[];
  value?: any;
}

export interface IStaticRegistrationStep {
  type: RegistrationStepType;
  options: IStaticRegistrationStepOption[];
}

export interface IEventRegistrationStaticData {
  registrationStepOptionModes: RegistrationStepOptionMode[];
  registrationSteps: IStaticRegistrationStep[];
}

export interface IEventSummary {
  id: number;
  title: string;
  slug: string;
  shortTitle: string | null;
  eventStartsAt: string | null;
  eventEndsAt: string | null;
  registrationStartsAt: string | null;
  registrationEndsAt: string | null;
  publishedAt: string | null;
  path: string;
  isLocked: boolean;
  collectionRelation: IEventCollectionRelationSummary | null;
  type: EventType;
  logoImage: IEventImage;
  backgroundImage: IEventImage;
  esportType: EsportEventType;
  mode: EventMode;
}

export interface IEventBracketShortSummary {
  id: number;
  title: string;
  slug: string;
  supportsPoints: boolean;
  icon: string | null;
  eventId: number;
  format: BracketFormat;
}

export interface IEventStandingFiltersResponse {
  collections: ICollectionWithEventBracketSummary[];
}

export interface ICollectionWithEventSummary {
  collection: IEventCollectionSummary;
  events: (IEventSummary & { brackets?: IEventBracketShortSummary[] })[];
}

export interface IEventWithBracketsSummary extends IEventSummary {
  brackets: IEventBracketShortSummary[];
}

export interface IEventAnalyticsFiltersResponse {
  teams: IEventBracketTeamShortDetails[];
  zoneIds: number[];
  affixes: TClientAffixData[][];
  collections: ICollectionWithEventSummary[];
  brackets: IEventBracketShortSummary[];
}

export interface IEventCollaboratorSummary {
  id: number;
  name: string;
}

export interface IHubCollectionEventBracketShortSummary {
  id: number;
  title: string;
  bracketEndsDate: string | null;
  format: BracketFormat;
}
export interface IBracketSpecRestrictionSummary {
  id: number;
  specId: number;
  bracketId: number;
  spec: TSpecSummary | null;
}

export interface IBracketReplayRestrictionSummary {
  id: number;
  replayKeystoneRunId: number;
  seasonSlug: string;
  bracketId: number;
}

export const enum RegistrationStepOptionMode {
  HIDDEN = 'hidden',
  REQUIRED = 'required',
  RECOMMENDED = 'recommended',
  OPTIONAL = 'optional'
}

export interface IEventRegistrationStepOption {
  label: TRegistrationStepOptionLabel;
  mode: RegistrationStepOptionMode;
  value?: TEventRegistrationStepOptionValue;
  supportsModes?: RegistrationStepOptionMode[];
  ordinal?: number;
}

export type TEventRegistrationStepOptionValue = any | TEventRegistrationStepBuilderValue;

export type TEventRegistrationStepBuilderValue =
  | TEventRegistrationStepBuilderInputValue
  | TEventRegistrationStepBuilderCheckboxValue
  | TEventRegistrationStepBuilderRadioValue
  | TEventRegistrationStepBuilderSliderValue
  | TEventRegistrationStepBuilderSelectValue
  | TEventRegistrationStepBuilderTextareaValue
  | TEventRegistrationStepBuilderSwitchValue;

export type TEventRegistrationStepBuilderValueKeys = keyof (TEventRegistrationStepBuilderInputValue &
  TEventRegistrationStepBuilderCheckboxValue &
  TEventRegistrationStepBuilderRadioValue &
  TEventRegistrationStepBuilderSliderValue &
  TEventRegistrationStepBuilderSelectValue &
  TEventRegistrationStepBuilderTextareaValue &
  TEventRegistrationStepBuilderSwitchValue);

type TEventRegistrationStepBuilderCommonValue = {
  label: string;
  csvLabel: string;
  helpText: string;
};

export type TEventRegistrationStepBuilderInputValue = TEventRegistrationStepBuilderCommonValue & {
  type: BuilderStepOptionType.Input;
  min?: number;
  max?: number;
};

export type TEventRegistrationStepBuilderTextareaValue = TEventRegistrationStepBuilderCommonValue & {
  type: BuilderStepOptionType.Textarea;
  min: number;
  max: number;
};

export type TEventRegistrationStepBuilderSwitchValue = TEventRegistrationStepBuilderCommonValue & {
  type: BuilderStepOptionType.Switch;
  enabledLabel: string;
  disabledLabel: string;
  inlineLabel: string;
};

export type TEventRegistrationStepBuilderSliderValue = TEventRegistrationStepBuilderCommonValue & {
  type: BuilderStepOptionType.Slider;
  min: number;
  max: number;
  steps: string;
};

export type TEventRegistrationStepBuilderSelectValue = TEventRegistrationStepBuilderCommonValue & {
  type: BuilderStepOptionType.Select;
  options: string[];
};

export type TEventRegistrationStepBuilderCheckboxValue = TEventRegistrationStepBuilderCommonValue & {
  type: BuilderStepOptionType.Checkbox;
  options: string[];
  minSelections?: number | null;
  maxSelections?: number | null;
  enableRanking?: boolean;
};

export type TEventRegistrationStepBuilderRadioValue = TEventRegistrationStepBuilderCommonValue & {
  type: BuilderStepOptionType.Radio;
  options: string[];
  inline?: boolean;
};

export interface IEventUserRegistrationStepSummary extends IEventRegistrationStepSummary {
  id?: number;
  preload?: any;
  validators?: any;
}

export interface IEventImage {
  path: string | null;
  storageItem: TShortStorageItemSummary | null;
}

export interface IEventDetails {
  id: number;
  title: string;
  shortTitle: string | null;
  privacy: EventPrivacy;
  category: EventCategory;
  namespace: string;
  slug: string;
  type: EventType;
  esportType: EsportEventType;
  eventTypeLabel: string | null;
  eventTypeLabelHtml?: string | null;
  prize: string | null;
  isLocked: boolean;
  teamsCount: number | null;
  minTeamSize: number | null;
  maxTeamSize: number | null;
  shortDescription: string | null;

  logoImage: IEventImage;
  logoImage2: IEventImage;
  headerImage: IEventImage;
  backgroundImage: IEventImage;

  statsImage: TShortStorageItemSummary | null;

  publishedAt: string | null;
  eventStartsAt: string | null;
  eventEndsAt: string | null;
  registrationStartsAt: string | null;
  registrationEndsAt: string | null;
  successfulDescription: string | null;
  successfulDescriptionHtml?: string | null;
  checkinSuccessfulDescription: string | null;
  checkinSuccessfulDescriptionHtml?: string | null;
  supportedRegions: string[];
  webhookGuildId: string | null;
  webhookChannelId: string | null;
  path: string;
  sections: IEventContentSummary[];
  registrationSteps: IEventRegistrationStepSummary[];
  collaborators: IEventCollaboratorSummary[];
  collections: IEventCollectionSummary[];
  isOwner?: boolean;
  usesBBCode: boolean;
  isEventLocked: boolean;
  videos: IEventVideoSummary[];
  winners: IEventTeamWinner[];
}

export interface IEventAdditionInfo {
  registration_found: {
    when: Date;
    areRemindersEnabled: boolean;
    isOwnerTeam: boolean;
    brackets: IUserRegistrationBracket[];
  } | null;
  yourTeam: IEventTeamDetails | null;
}

export interface IPublishedEventSummary {
  id: number;
  title: string;
  imagePath: string | null;
  shortTitle: string;
  imageUrl: string | null;
  imageLogoPath: string | null;
  imageLogo: TShortStorageItemSummary | null;
  supportedRegions: string[];
  mode: EventMode;
  path: string;
  esportType: EsportEventType;
  registrationStartsAt?: string | null;
  registrationEndsAt?: string | null;
  sections: IEventContentSummary[];
  slug: string;
  eventStartsAt: string | null;
  eventEndsAt: string | null;
  type: EventType;
  namespace: string;
  videos: IEventVideoSummary[];
  brackets: IHubCollectionEventBracketShortSummary[];
}

export interface IEventCollectionSummary {
  id: number;
  slug: string;
  name: string;
  type: EventCollectionType;
  description: string | null;
  imageLogo: string | null;
  imageLogoId?: number | null;
  statsImageId?: number | null;
  events: (IEventSummary & { registrationSteps?: IEventRegistrationStepSummary[] })[];
  winners?: IEventTeamWinner[];
  videos?: IEventVideoSummary[];
  creatorUserId: number;
  expansionSlug: string;
}

export interface IEventHubCollectionEventSummary extends IPublishedEventSummary {}

export interface ILiveEventCoverageInfo {
  slug: string;
  baseUrl: string;
  limit: number;
}

export interface ILiveEventSummary {
  event: IPublishedEventSummary;
  bracket?: IPublishedBracketSummary | null;
  eventRegistrationDetails: IEventRegistrationDetails;
  coverageInfo?: ILiveEventCoverageInfo | null;
}

export interface IEventHubCollectionSummary extends Omit<IEventCollectionSummary, 'events'> {
  events: IEventHubCollectionEventSummary[];
  videos: IEventVideoSummary[];
  statCollectionId: number | null;
  imageLogoItem: TShortStorageItemSummary | null;
}

export interface IEventTeamWinner {
  id: number;
  place: number;
  team: IEventTeamWinnerDetails;
  region?: TRegionResource;
  subRegion?: TSubRegionResource;
  bracketGroup?: string | null;
}

export interface IEventCollectionRelationSummary {
  id: number;
  eventId: number;
  collectionId: number;
}

export interface IEventRegistrationDetails extends IEventDetails {
  namespace: string;
  currentTeamsCount: number;
  isRegistrationActive: boolean;
  isEventStarted: boolean;
  isCreator: boolean;
  isCollaborator: boolean;
  eventInfo: IEventAdditionInfo;
  sections: IEventContentSummary[];
  registrationSteps: IEventUserRegistrationStepSummary[];
  multiRegistrationEvents?: IMultiRegistrationEvent[];
  multiRegistrationDetails?: IMultiRegistrationEventsDetails | null;
}

export const enum EventTeamStatus {
  AWAITING_ELIGIBILITY = 'awaiting-eligibility',
  ELIGIBLE = 'eligible',
  NOT_ELIGIBLE = 'not-eligible'
}

export type IEventBracketTeamSummary = {
  id: number;
  bracketId: number;
  teamId: number;
  bracketTeamName: string;
  regionId: number | null;
  subRegionId: number | null;
  registered: boolean;
  seed: number;
  checkedInAt: string | null;
  checkedOutAt: string | null;
  points: number;
  noteHtml: string | null;
  noteIcon: string | null;
  createdAt: string;
};

export interface IEventTeamDetails {
  id: number;
  groupId: number;
  name: string;
  type: number;
  slug: string;
  region: TRegionResource;
  teamEventProfileUrl: string;
  owner: any;
  faction: string;
  members: any[];
  inviteCode?: string;
  inviteCodeExpiresAt?: string;
  status: EventTeamStatus;
  bracketTeam?: IEventBracketTeamSummary | null;
}

export interface IEventTeamWinnerDetails {
  id?: number;
  groupId?: number;
  name: string | null;
  namespace?: string;
  iconLogoUrl: string | null;
  iconLogo?: TShortStorageItemSummary | null;
  type?: number;
  slug?: string;
  teamEventProfileUrl: string | null;
  owner?: any;
  bracketTeamName?: string | null;
}

export interface IEventTeamList {
  teamList: IEventTeamDetails[];
  count: number;
}

export interface IShortRelatedEvent {
  id: number;
  title: string;
  slug: string;
}

export interface IPublicRelatedEvent extends IEventSummary {
  brackets: IEventBracketSummary[];
}

export enum EventCollectionType {
  STATISTIC = 'statistic',
  AUTO_TEAM_REGISTRATION = 'register',
  HUB = 'hub',
  EXPANSION = 'expansion',
  MULTI_REGISTRATION = 'multi-registration'
}

export enum EventContentScope {
  ALWAYS_VISIBLE = 'always-visible',
  ONLY_EVENT = 'only-event',
  ONLY_HUB = 'only-hub'
}

export interface ICreateEventCollectionPayload {
  name: string;
  type: EventCollectionType;
  imageLogo?: string | null;
  imageLogoId?: number | null;
  description?: string | null;
  expansionSlug?: string | null;
  statsImageId?: number | null;
  eventSlug?: string | null;
}

export interface IEditEventCollectionPayload extends Omit<Required<ICreateEventCollectionPayload>, 'eventSlug'> {}

export interface IEventWinnerPayload {
  place: number;
  regionSlug?: string;
  subRegionSlug?: string;
  eventTeamId?: number;
  name?: string | null;
  iconUrl?: string | null;
  teamUrl?: string | null;
}

export type TLegacyEventParams = {
  title: string;
  slug: string;
  shortTitle: string | null;
  logoImagePath: string | null;
  logoImageId: number | null;
  esportType: EsportEventType;
  sections: IEventContentSummary[];
  privacy: string;
  category: EventCategory;
  publishedDate: string | null;
  eventEndsDate: string | null;
};

export type TUpdateEventVideosPayload = {
  entityId: number;
  entityType: TEventVideoEntityType;
  videos: Pick<IEventVideoSummary, 'ordinal' | 'id'>[];
};

export type TGetEventVideosQueryParams = {
  entityId: number;
  entityType: TEventVideoEntityType;
};

export type TEditEventVideoPayload = {
  title: string;
  videoUrl: string;
  withTimestamp: boolean;
  tags: TEventVideoTags[];
  bracketGroup?: string | null;
};

export type TAddEventVideoPayload = TEditEventVideoPayload & {
  entityId: number;
  entityType: TEventVideoEntityType;
};

export enum LiveEventMode {
  AWC = 'awc',
  MDI = 'mdi',
  HOME = 'home',
  OTHER = 'other'
}

export interface IGetEventInsightsFiltersParams {
  eventSlug?: string;
  bracketSlug?: string;
  collectionId?: number;
}

export interface ICollectionWithEventBracketSummary {
  collection: IEventCollectionSummary;
  events: (IEventSummary & {
    brackets: IEventBracketShortSummary[];
  })[];
}

export type TRequiredRegistrationEvent = {
  event: {
    id: number;
    title: string;
    slug: string;
    image_logo: string | null;
    image_url: string | null;
    header_image: TShortStorageItemSummary | null;
  };
  team: TTeamSummary;
};
export enum EventTeamMemberHistoryStatus {
  JOINED = 'joined', // member joined the team
  KICKED = 'kicked', // member was kicked from the team by captain
  LEFT = 'left' // member left the team of own accord
}
export type TEventCsvRules = {
  restricted?: boolean;
};

export interface IMultiRegistrationEvent {
  event: {
    id: number;
    title: string;
    slug: string;
  };
  isUserRegistered: boolean;
}

export interface IMultiRegistrationEventsDetails {
  team?: IEventTeamDetails | null;
  teamRegistrations?: IMultiRegistrationEventTeamRegistration[] | null;
}

export interface IMultiRegistrationEventTeamRegistration extends IMultiRegistrationEvent {
  event: {
    id: number;
    title: string;
    slug: string;
    minTeamSize?: number | null;
  };
  teamStatus: EventTeamStatus;
  membersRegistration: Record<number, boolean>;
}

export type TSaveEventUserRegistrationParams = {
  slug: string;
  data: {
    steps: IEventRegistrationUserStepData[];
    multiRegistrationEvents: TMultiRegistrationEventFormItem[];
  };
  inviteCode?: string;
};

export type TMultiRegistrationEventFormItem = {
  id: number;
  checked: boolean;
};

export type TEventRegistrationSuccessResult = {
  event: IEventInstance;
  user: ISharedUserInstance;
  isAutoRegistration: boolean;
  isMultipleRegistrationEvent: boolean;
  isNewRegistration: boolean;
};

export type TEventRegistrationFailureResult = {
  event: IEventInstance;
  user: ISharedUserInstance;
  isAutoRegistration: boolean;
  isMultipleRegistrationEvent: boolean;
  error: unknown;
};

export type TEventRegistrationReregisterResult = {
  successResults: TEventRegistrationSuccessResult[];
  failureResults: TEventRegistrationFailureResult[];
  groupCharter?: ISharedGroupCharterInstance | null;
  requestEvent: IEventInstance;
};

export type TLatestEventConfigItem = {
  type: EsportEventType;

  // Or collectionId or eventSlug
  collectionId?: number;
  eventSlug?: string;

  // If bracketSlug is provided, eventSlug is required
  bracketSlug?: string;

  // if provided, the keystone rankings will be limited to this number of teams
  keystoneRankingsLimit?: number;

  regionSlug?: string;
  subRegionSlug?: string;

  hiddenAt?: string;
};

export type TUserRegistrationDetails = {
  userData: TEventRegistrationUserRegistrationDataMap[];
};

export type TEventRegistrationUserRegistrationDataMap = Partial<
  Record<TRegistrationStepOptionLabel, TEventRegistrationUserRegistrationData>
>;

export type TEventRegistrationUserRegistrationData = {
  value: TUserEventRegistrationData;
  option: IEventRegistrationStepOption;
  step: {
    ordinal: number;
    type: RegistrationStepType;
    title: string;
    id: number;
  };
};

export type TUserEventRegistrationData = any;

export type TLatestEventData = TLatestEventDataSuccess | TLatestEventDataError;

export type TLatestEventDataSuccess = {
  event: IPublishedEventSummary;
  collection?: IEventCollectionSummary | null;
  bracket?: IPublishedBracketSummary | null;
  standings?: TResultStandingAggregationList | null;
  rankings?: any[] | null;
};

export type TLatestEventDataError = {
  error?: string | null;
};
